import { Box } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { zodResolver } from '@hookform/resolvers/zod'
import { formatDateTimeWithSeconds } from '../../../../app/utils'
import { useState } from 'react'
import { useLoggedInUser } from '../../../../app/hooks/useLoggedInUser'
import {
  useCreateProductMutation,
  useUpdateProductMutation,
} from '../../../../app/redux-fetch/apiProducts'
import snackbarUtil from '../../../../app/snackbar/snackbarUtil'
import SubmitButtons from '../../../../components/Buttons/SubmitButtons'
import { useSideModalContext } from '../../../Assets/modals/SideModalContext'
import { IProduct } from '../../types'
import ProductFormTabs from './ProductFormTabs'
import { getProductValidationSchema, ProductSchemaType } from './productValidationSchema'

interface ExtendedProductSchema extends ProductSchemaType {
  isNotDimensions?: boolean
  isRectangular?: boolean
}

type ProductFormProps = {
  product: ProductSchemaType
  isDisabled: boolean
  onSubmit?: (data: any) => void
}

const isTemperatureSettingEmpty = (
  temperatureSetting: ProductSchemaType['temperatureSetting']
): boolean => {
  if (!temperatureSetting) return true
  const hasNoRange = !temperatureSetting.temperatureRangeId || temperatureSetting.temperatureRangeId === 0
  const hasNoCriticalThresholds =
    temperatureSetting.upperThresholdCritical == null || temperatureSetting.lowerThresholdCritical == null
  return hasNoRange && hasNoCriticalThresholds
}

const ProductForm = ({ product, isDisabled, onSubmit }: ProductFormProps) => {
  const { onClose } = useSideModalContext()
  const { formatMessage } = useIntl()
  const { tenantId } = useLoggedInUser()
  const isNew = !product.id
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [createProduct] = useCreateProductMutation()
  const [updateProduct] = useUpdateProductMutation()
  const validationSchema = getProductValidationSchema(formatMessage)

  const initPhysicalPropertiesSwitches = (
    productData: ProductSchemaType
  ): ExtendedProductSchema => {
    const updatedProduct = { ...productData } as ExtendedProductSchema

    let isNotDimensions = false
    let isRectangular = true

    if (updatedProduct.physicalProperties) {
      const existingProps = updatedProduct.physicalProperties
      const { volume, length, width, height } = existingProps as any
      if (volume !== undefined && volume !== null) {
        isNotDimensions = true
      } else if (length !== undefined || width !== undefined || height !== undefined) {
        isNotDimensions = false
      }

      if (width !== undefined) {
        isRectangular = false
      }
    }

    if (!updatedProduct.physicalProperties) {
      updatedProduct.physicalProperties = {}
    }

    updatedProduct.isNotDimensions = isNotDimensions
    updatedProduct.isRectangular = isRectangular
    return updatedProduct
  }

  const initializedProduct = initPhysicalPropertiesSwitches(product)

  const methods = useForm<ExtendedProductSchema>({
    defaultValues: initializedProduct as ExtendedProductSchema,
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
  })

  const {
    handleSubmit,
    formState: { errors, isDirty, isValid },
    reset,
  } = methods

    const onFormSubmit = async (values: ExtendedProductSchema) => {
      try {
        if (!isDirty) {
          onClose()
        }
        if (!isValid && errors && typeof errors === 'object' && Object.keys(errors).length > 0) {
          snackbarUtil.error(
            formatMessage({ id: 'product.validation.error', defaultMessage: 'Please fix the validation errors before saving' })
          )
          return
        }

      setIsSubmitting(true)

      const { isNotDimensions, isRectangular, ...cleanValues } = values

      const flatProductData = {
        ...cleanValues,
        temperatureSetting: isTemperatureSettingEmpty(cleanValues.temperatureSetting)
          ? null
          : cleanValues.temperatureSetting,
        prices: cleanValues.prices?.map((price) => ({
          ...price,
          priceValidTo:
            price.priceValidTo === '∞' || price.priceValidTo === 'INFINITY'
              ? null
              : price.priceValidTo
                ? formatDateTimeWithSeconds(price.priceValidTo)
                : price.priceValidTo,
          priceValidFrom: price.priceValidFrom
            ? formatDateTimeWithSeconds(price.priceValidFrom)
            : price.priceValidFrom,
          validFrom: price.validFrom,
          validTo: price.validTo,
        })),
      }
      const productWithStringDates = {
        ...flatProductData,
        prices: flatProductData.prices?.map((price) => ({
          ...price,
          validFrom: price.validFrom,
          validTo: price.validTo,
        })),
      }
      if (!values.id) {
        const res = await createProduct({
          tenantId,
          customerId: values.customerId ?? -1,
          body: productWithStringDates as IProduct,
        }).unwrap()

        snackbarUtil.success(
          formatMessage({ id: 'product.created', defaultMessage: 'Product created successfully' })
        )
        onSubmit && onSubmit(res)
        onClose()
      } else {
        const res = await updateProduct({
          tenantId,
          customerId: values.customerId ?? -1,
          body: flatProductData as IProduct,
        }).unwrap()

        snackbarUtil.success(
          formatMessage({ id: 'product.updated', defaultMessage: 'Product updated successfully' })
        )
        onSubmit && onSubmit(res)
        onClose()
      }

      reset(values)
    } catch (error) {
      console.error('Error submitting product form:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const onInvalid = () => {
    snackbarUtil.error(
      formatMessage({ id: 'product.validation.error', defaultMessage: 'Please fix the validation errors before saving' })
    )
  }

  const onSubmitHandler = handleSubmit(onFormSubmit, onInvalid)
  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmitHandler}>
        <Box sx={{ pl: '16px', pr: '16px', pt: '32px', m: '32px', mb: '0px' }}>
          <ProductFormTabs isDisabled={isDisabled} customerId={product.customerId} />
        </Box>
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            width: '100%',
            padding: '16px',
            backgroundColor: '#fff',
            borderTop: '1px solid #e0e0e0',
          }}
        >
          {!isDisabled && (
            <SubmitButtons
              create={isNew}
              isEnabled={!isSubmitting}
              submitForm={onSubmitHandler}
              onCancel={onClose}
            />
          )}
        </Box>
      </form>
    </FormProvider>
  )
}

export default ProductForm
